<template>
  <div class="page-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">创建收货入库单</h1>
      <div class="header-right">
        <button class="save-btn" @click="handleSave" :disabled="!canSave || saving">
          {{ saving ? '保存中...' : '保存' }}
        </button>
      </div>
    </header>

    <main class="content">
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div class="form-section">
        <h3 class="section-title">选择供应商</h3>

        <div class="form-group" ref="supplierGroupRef">
          <input
            v-model="supplierKeyword"
            class="search-input"
            placeholder="请输入供应商名称搜索"
            @input="handleSupplierSearch"
            @focus="handleSupplierFocus"
          />
          <div v-if="showSupplierDropdown" class="dropdown-menu" @pointerdown.stop>
            <div v-if="supplierLoading" class="dropdown-loading">加载中...</div>
            <div v-else-if="supplierList.length === 0" class="dropdown-empty">暂无数据</div>
            <div
              v-else
              v-for="s in supplierList"
              :key="s.id"
              class="dropdown-item"
              @click="selectSupplier(s)"
            >
              {{ s.supplierName }}
            </div>
            <div v-if="!supplierLoading && supplierTotalPages > 1" class="dropdown-footer">
              <button class="mini-btn" @click.stop="changeSupplierPage(-1)" :disabled="supplierCurrent <= 1">上一页</button>
              <span class="mini-page">{{ supplierCurrent }} / {{ supplierTotalPages }}</span>
              <button class="mini-btn" @click.stop="changeSupplierPage(1)" :disabled="supplierCurrent >= supplierTotalPages">下一页</button>
            </div>
          </div>

          <div v-if="selectedSupplier" class="selected-item">
            {{ selectedSupplier.supplierName }}
            <button class="clear-btn" @click="clearSupplier">×</button>
          </div>
        </div>
      </div>

      <div class="form-section" :class="{ disabled: !selectedSupplier }">
        <h3 class="section-title">选择仓库</h3>

        <div class="form-group">
          <select v-model="selectedWareId" class="select" :disabled="!selectedSupplier || wareLoading" @change="handleWareSelect">
            <option value="" disabled>{{ wareLoading ? '加载中...' : '请选择仓库' }}</option>
            <option v-for="w in wareList" :key="w.id" :value="w.id">{{ w.wareName }}</option>
          </select>
        </div>
      </div>

      <div class="form-section" :class="{ disabled: !selectedSupplier || !selectedWareId }">
        <div class="section-header">
          <h3 class="section-title">选择物料</h3>
          <div class="section-actions">
            <button class="mini-btn" @click="openMaterialModal" :disabled="!selectedSupplier || !selectedWareId">新增</button>
            <button class="mini-btn" @click="fetchMaterialPage({ reset: true })" :disabled="!selectedSupplier || !selectedWareId || materialLoading">
              {{ materialLoading ? '加载中...' : '刷新' }}
            </button>
          </div>
        </div>

        <div v-if="materialLoading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>

        <div v-else-if="materialList.length === 0" class="empty-state">
          <img src="@/assets/无数据.svg" alt="暂无数据" class="empty-img" />
          <p class="empty-text">~ 暂无数据 ~</p>
        </div>

        <div v-else class="selected-wrapper">
          <div v-if="selectedDetails.length === 0" class="empty-selected">
            <p class="empty-selected-text">请点击“新增”选择物料</p>
          </div>

          <div v-else class="selected-list">
            <div class="selected-card" v-for="(d, idx) in selectedDetails" :key="d.materialId">
              <div class="selected-card-header">
                <div class="selected-code mono">{{ d.materialNo || '-' }}</div>
                <button class="delete-btn" @click="removeDetail(idx)" aria-label="删除">×</button>
              </div>
              <div class="selected-card-body">
                <div class="selected-row">
                  <span class="label">物料描述</span>
                  <span class="value">{{ d.materialDesc || '-' }}</span>
                </div>
                <div class="selected-row">
                  <span class="label">当前库存</span>
                  <span class="value">{{ d.stock }}</span>
                </div>
                <div class="selected-row">
                  <span class="label required">入库数量</span>
                  <input class="qty-input" type="number" min="1" step="1" v-model="d.wareInNum" @change="normalizeQty(d)" />
                </div>
                <div class="selected-row">
                  <span class="label">备注</span>
                  <input class="remark-input" type="text" v-model="d.remark" placeholder="可选" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 物料选择弹窗 -->
    <div v-if="materialModalVisible" class="modal-overlay" @click.self="closeMaterialModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">选择物料</h3>
          <button class="modal-close" @click="closeMaterialModal" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div v-if="materialLoading" class="modal-loading">加载中...</div>
          <div v-else-if="materialList.length === 0" class="modal-empty">暂无数据</div>
          <div v-else class="modal-list">
            <div class="modal-item" v-for="m in materialList" :key="m.id">
              <div class="modal-item-main">
                <div class="modal-item-code mono">{{ m.materialNo || '-' }}</div>
                <div class="modal-item-desc">{{ m.materialDesc || '-' }}</div>
                <div class="modal-item-stock">库存：{{ formatStock(m) }}</div>
              </div>
              <button class="mini-btn" @click="addMaterial(m)" :disabled="isAdded(m.id)">
                {{ isAdded(m.id) ? '已添加' : '添加' }}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="mini-btn" @click="changeMaterialPage(-1)" :disabled="materialCurrent <= 1">上一页</button>
          <span class="mini-page">{{ materialCurrent }} / {{ materialTotalPages }}</span>
          <button class="mini-btn" @click="changeMaterialPage(1)" :disabled="materialCurrent >= materialTotalPages">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { getUicAuthUserInfo } from '@/utils/auth'
import toast from '@/utils/toast'
import { getSupplierPageGtZero, getWareList, getMaterialPage, submitWareIn } from '@/api/wareInReceiveCreate'

export default {
  name: 'WareInReceiveCreate',
  setup() {
    const router = useRouter()

    const statusMsg = ref('')
    const statusType = ref('info')

    const handleBack = () => {
      router.back()
    }

    const saving = ref(false)

    const bizDept = ref('')

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      const userInfo = getUicAuthUserInfo()
      bizDept.value = userInfo?.deptId || ''
      if (!bizDept.value) {
        statusMsg.value = '未获取到项目组信息(bizDept)'
        statusType.value = 'warn'
      }
    })

    // 供应商
    const supplierKeyword = ref('')
    const supplierCurrent = ref(1)
    const supplierSize = ref(20)
    const supplierTotal = ref(0)
    const supplierLoading = ref(false)
    const supplierList = ref([])
    const selectedSupplier = ref(null)
    const showSupplierDropdown = ref(false)

    const supplierGroupRef = ref(null)

    const supplierTotalPages = computed(() => {
      const total = supplierTotal.value || 0
      const size = supplierSize.value || 20
      return Math.max(1, Math.ceil(total / size))
    })

    const supplierSearchTimer = ref(0)

    const supplierFetchSeq = ref(0)

    const fetchSupplierPage = async ({ reset } = {}) => {
      const seq = ++supplierFetchSeq.value
      supplierLoading.value = true
      try {
        const res = await getSupplierPageGtZero({
          supplierName: supplierKeyword.value.trim(),
          current: supplierCurrent.value,
          size: supplierSize.value
        })
        if (seq !== supplierFetchSeq.value) return
        const payload = res?.data ?? res
        const records =
          payload?.records ||
          payload?.data?.records ||
          payload?.list ||
          []
        const total =
          payload?.total ??
          payload?.data?.total ??
          (Array.isArray(records) ? records.length : 0)

        supplierList.value = Array.isArray(records) ? records : []
        supplierTotal.value = typeof total === 'number' ? total : Number(total || 0)
        showSupplierDropdown.value = true
      } catch (e) {
        if (seq !== supplierFetchSeq.value) return
        supplierList.value = []
        supplierTotal.value = 0
        toast.error(e?.message || '供应商加载失败')
      } finally {
        if (seq === supplierFetchSeq.value) {
          supplierLoading.value = false
        }
      }

      if (reset) {
        await nextTick()
      }
    }

    const handleSupplierSearch = () => {
      showSupplierDropdown.value = true
      if (supplierSearchTimer.value) window.clearTimeout(supplierSearchTimer.value)
      supplierSearchTimer.value = window.setTimeout(async () => {
        supplierCurrent.value = 1
        await fetchSupplierPage({ reset: true })
      }, 250)
    }

    const handleSupplierFocus = async () => {
      showSupplierDropdown.value = true
      if (!supplierLoading.value && supplierList.value.length === 0) {
        supplierCurrent.value = 1
        await fetchSupplierPage({ reset: true })
      }
    }

    const changeSupplierPage = async (delta) => {
      const next = supplierCurrent.value + delta
      if (next < 1 || next > supplierTotalPages.value) return
      supplierCurrent.value = next
      await fetchSupplierPage()
    }

    const selectSupplier = async (s) => {
      selectedSupplier.value = s
      showSupplierDropdown.value = false
      supplierKeyword.value = ''

      clearWare()
      await fetchWares()
    }

    const clearSupplier = () => {
      selectedSupplier.value = null
      clearWare()
      clearMaterials()
    }

    // 仓库
    const wareLoading = ref(false)
    const wareList = ref([])
    const selectedWareId = ref('')

    const clearWare = () => {
      wareList.value = []
      selectedWareId.value = ''
    }

    const fetchWares = async () => {
      if (!selectedSupplier.value) return
      if (!bizDept.value) return
      if (wareLoading.value) return

      wareLoading.value = true
      try {
        const res = await getWareList({ bizDept: bizDept.value })
        wareList.value = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        wareList.value = []
        toast.error(e?.message || '仓库加载失败')
      } finally {
        wareLoading.value = false
      }
    }

    const handleWareSelect = async () => {
      clearMaterials()
      selectedDetails.value = []
    }

    // 物料
    const materialCurrent = ref(1)
    const materialSize = ref(20)
    const materialTotal = ref(0)
    const materialLoading = ref(false)
    const materialList = ref([])

    const materialTotalPages = computed(() => {
      const total = materialTotal.value || 0
      const size = materialSize.value || 20
      return Math.max(1, Math.ceil(total / size))
    })

    const clearMaterials = () => {
      materialCurrent.value = 1
      materialTotal.value = 0
      materialList.value = []
    }

    const fetchMaterialPage = async ({ reset } = {}) => {
      if (!selectedSupplier.value?.id || !selectedWareId.value) return
      if (materialLoading.value) return

      materialLoading.value = true
      try {
        if (reset) materialCurrent.value = 1
        const res = await getMaterialPage({
          current: materialCurrent.value,
          size: materialSize.value,
          supplierId: selectedSupplier.value.id,
          storeId: selectedWareId.value,
          isBad: 0
        })
        materialList.value = res?.data?.records || []
        materialTotal.value = res?.data?.total || 0
      } catch (e) {
        materialList.value = []
        materialTotal.value = 0
        toast.error(e?.message || '物料加载失败')
      } finally {
        materialLoading.value = false
      }
    }

    const changeMaterialPage = async (delta) => {
      const next = materialCurrent.value + delta
      if (next < 1 || next > materialTotalPages.value) return
      materialCurrent.value = next
      await fetchMaterialPage()
    }

    const formatStock = (m) => {
      const candidates = ['stockNum', 'stockQty', 'remainQty', 'remainNum', 'totalStockNum', 'qty', 'num']
      for (const key of candidates) {
        const v = m?.[key]
        if (v !== undefined && v !== null && v !== '') return v
      }
      return '-'
    }

    // 已选物料明细
    const selectedDetails = ref([])

    const isAdded = (materialId) => {
      return selectedDetails.value.some(d => d.materialId === materialId)
    }

    const normalizeQty = (d) => {
      const n = Number(d.wareInNum)
      if (!Number.isFinite(n) || n <= 0) d.wareInNum = '1'
      else d.wareInNum = String(Math.floor(n))
    }

    const addMaterial = (m) => {
      if (!m?.id || isAdded(m.id)) return
      selectedDetails.value.push({
        materialId: m.id,
        materialNo: m.materialNo || '',
        materialDesc: m.materialDesc || '',
        stock: formatStock(m),
        wareInNum: '1',
        imageRemark: '',
        remark: ''
      })
    }

    const removeDetail = (idx) => {
      selectedDetails.value.splice(idx, 1)
    }

    // 物料弹窗
    const materialModalVisible = ref(false)

    const openMaterialModal = async () => {
      if (!selectedSupplier.value?.id || !selectedWareId.value) return
      materialModalVisible.value = true
      if (materialList.value.length === 0) {
        await fetchMaterialPage({ reset: true })
      }
    }

    const closeMaterialModal = () => {
      materialModalVisible.value = false
    }

    const canSave = computed(() => {
      if (!selectedSupplier.value?.id) return false
      if (!selectedWareId.value) return false
      if (selectedDetails.value.length === 0) return false
      return selectedDetails.value.every(d => Number(d.wareInNum) > 0)
    })

    const handleSave = async () => {
      if (!canSave.value || saving.value) return

      saving.value = true
      try {
        const payload = {
          wareInType: '0',
          storeId: selectedWareId.value,
          supplierId: selectedSupplier.value.id,
          supplierName: selectedSupplier.value.supplierName,
          wareInDetailList: selectedDetails.value.map(d => ({
            materialId: d.materialId,
            wareInNum: String(d.wareInNum),
            imageRemark: d.imageRemark || '',
            remark: d.remark || ''
          }))
        }

        await submitWareIn(payload)
        toast.success('保存成功')
        router.push('/warein-receive')
      } catch (e) {
        toast.error(e?.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    // 初次进入时给出默认供应商列表（不输入也能选）
    onMounted(async () => {
      await fetchSupplierPage({ reset: true })
    })

    // 点击空白处关闭下拉
    const handlePointerDownOutside = (event) => {
      const root = supplierGroupRef.value
      if (root && root.contains(event.target)) return
      showSupplierDropdown.value = false
    }

    onMounted(() => {
      document.addEventListener('pointerdown', handlePointerDownOutside, true)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
    })

    return {
      statusMsg,
      statusType,
      handleBack,
      handleSave,
      canSave,
      saving,

      supplierKeyword,
      supplierList,
      supplierLoading,
      supplierCurrent,
      supplierTotalPages,
      selectedSupplier,
      showSupplierDropdown,
      supplierGroupRef,
      handleSupplierSearch,
      handleSupplierFocus,
      changeSupplierPage,
      selectSupplier,
      clearSupplier,

      wareList,
      wareLoading,
      selectedWareId,
      handleWareSelect,

      materialList,
      materialLoading,
      materialCurrent,
      materialTotalPages,
      fetchMaterialPage,
      changeMaterialPage,
      formatStock
      ,
      materialModalVisible,
      openMaterialModal,
      closeMaterialModal,
      addMaterial,
      isAdded,
      selectedDetails,
      removeDetail,
      normalizeQty
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background: var(--background-color);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
}

.header-right {
  display: flex;
  justify-content: flex-end;
  min-width: 64px;
}

.save-btn {
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.save-btn:disabled {
  opacity: 0.6;
}

.back-btn {
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.icon-back {
  width: 18px;
  height: 18px;
}

.title {
  font-size: 16px;
  font-weight: 600;
}

.content {
  flex: 1;
  padding: 16px;
}

.form-section {
  background: var(--card-background);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px var(--shadow-light);
}

.form-section.disabled {
  opacity: 0.6;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-actions {
  display: flex;
  gap: 8px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--text-primary);
}

.form-group {
  position: relative;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
}

.select {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--card-background);
}

.dropdown-menu {
  position: absolute;
  top: 44px;
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 8px 20px var(--shadow-light);
  z-index: 9999;
}

.dropdown-item {
  padding: 10px 12px;
  font-size: 14px;
  color: var(--text-primary);
}

.dropdown-item:active {
  background: var(--background-color);
}

.dropdown-empty,
.dropdown-loading {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.dropdown-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid var(--border-color);
}

.selected-item {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(9, 109, 217, 0.1);
  border: 1px solid rgba(186, 231, 255, 0.5);
  color: #096dd9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
}

.clear-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 18px;
  cursor: pointer;
}

.status-banner {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
}

.status-banner.info {
  background: #ebf8ff;
  color: #2b6cb0;
}

.status-banner.warn {
  background: #fffaf0;
  color: #c05621;
}

.status-banner.error {
  background: #fff5f5;
  color: #c53030;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: var(--text-secondary);
  font-size: 13px;
}

.empty-state {
  text-align: center;
  padding: 16px 0;
}

.empty-img {
  width: 90px;
  opacity: 0.8;
}

.empty-text {
  color: var(--text-secondary);
  font-size: 13px;
  margin-top: 6px;
}

.table-wrapper {
  overflow-x: auto;
}

.selected-wrapper {
  margin-top: 8px;
}

.empty-selected {
  padding: 12px 0;
  text-align: center;
}

.empty-selected-text {
  font-size: 13px;
  color: var(--text-secondary);
}

.selected-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.selected-card {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
}

.selected-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--background-color);
}

.selected-card-body {
  padding: 10px 12px;
}

.selected-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
}

.label {
  color: var(--text-secondary);
  flex: 0 0 auto;
}

.label.required {
  color: #c53030;
}

.value {
  color: var(--text-primary);
  text-align: right;
  flex: 1 1 auto;
}

.qty-input,
.remark-input {
  height: 34px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0 10px;
  flex: 1 1 auto;
}

.delete-btn {
  width: 26px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: var(--card-background);
  color: #c53030;
  font-size: 18px;
}

/* modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}

.modal-container {
  width: 100%;
  max-width: 560px;
  background: var(--card-background);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 86vh;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: var(--background-color);
  font-size: 18px;
}

.modal-body {
  padding: 10px 14px;
  overflow-y: auto;
}

.modal-footer {
  padding: 12px 14px;
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.modal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);
}

.modal-item-main {
  min-width: 0;
  flex: 1 1 auto;
}

.modal-item-code {
  font-size: 13px;
  color: var(--text-primary);
}

.modal-item-desc,
.modal-item-stock {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-loading,
.modal-empty {
  padding: 20px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  border-bottom: 1px solid var(--border-color);
  padding: 10px 8px;
  font-size: 13px;
  text-align: left;
}

.table th {
  color: var(--text-secondary);
  font-weight: 600;
  background: var(--background-color);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.num {
  text-align: right;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
}

.mini-btn {
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: var(--primary-color);
  color: #fff;
  font-size: 12px;
}

.mini-btn:disabled {
  opacity: 0.6;
}

.mini-page {
  font-size: 12px;
  color: #718096;
}
</style>
